import type { AttachedFile } from './api'

// Pure helpers for the voice-notes recorder — kept free of browser media APIs so
// they are unit-testable.

export const NOTE_STYLE_STORAGE_KEY = 'hj:selected-note-style'

export function loadStoredNoteStyleId(): string | null {
  try {
    return localStorage.getItem(NOTE_STYLE_STORAGE_KEY)
  } catch {
    return null
  }
}

export function storeNoteStyleId(id: string | null): void {
  try {
    if (id) localStorage.setItem(NOTE_STYLE_STORAGE_KEY, id)
    else localStorage.removeItem(NOTE_STYLE_STORAGE_KEY)
  } catch {
    // storage unavailable (private mode) — selection just won't persist
  }
}

export interface TranscriptSegment {
  index: number
  text?: string
  error?: string
}

export function formatElapsed(totalSec: number): string {
  const sec = Math.max(0, Math.floor(totalSec))
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

/**
 * Joins transcribed segments in recording order. Each segment is diarized
 * independently, so "Speaker A" can be a different voice in the next part —
 * when there is more than one part, each is labelled so a reader (or the AI
 * summarising it) knows the speaker labels restart. A failed segment becomes an
 * explicit note rather than silently vanishing minutes of conversation.
 */
export function assembleTranscript(segments: TranscriptSegment[]): string {
  const parts = [...segments]
    .sort((a, b) => a.index - b.index)
    .map((s) =>
      s.error
        ? `[This part of the recording could not be transcribed: ${s.error}]`
        : (s.text ?? '').trim(),
    )
    .filter((t) => t.length > 0)
  if (parts.length <= 1) return parts[0] ?? ''
  return parts.map((t, i) => `[Part ${i + 1} of ${parts.length}]\n${t}`).join('\n\n')
}

// btoa cannot take raw UTF-8 (throws on anything outside Latin-1), so encode
// first; chunked to keep String.fromCharCode off the argument-spread limit.
export function utf8ToBase64(text: string): string {
  const bytes = new TextEncoder().encode(text)
  let bin = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  return btoa(bin)
}

/**
 * The customer the conversation is pinned to (chosen in the composer, or
 * resolved by the AI). General context, not note-specific: every
 * customer-scoped ask refers to them until the operator names someone else.
 * The type name predates that widening.
 */
export interface NoteCustomer {
  id: string
  name: string
}

/**
 * The message text that accompanies an attached transcript. It must SAY what is
 * attached and what should happen ("Write this up as a note." alone read as an
 * unexplained instruction), and when a customer is pinned it names them in the
 * TEXT. The id deliberately does NOT appear here: this string prefills the
 * composer, so the operator would read a raw uuid — and the pin already
 * delivers the id server-side (CUSTOMER CONTEXT) on every send while pinned.
 */
export function buildTranscriptHandoff(customer: NoteCustomer | null): string {
  const base = 'Here is the transcription and summary of a recorded conversation. Write it up as a note'
  return customer ? `${base} for ${customer.name}.` : `${base}.`
}

/**
 * Combines the AI recap and the transcript into the single text that attaches
 * to the message. Labelled sections so both the operator and the note-writing
 * AI can tell summary from verbatim transcript.
 */
export function composeTranscriptText(summary: string, transcript: string): string {
  const s = summary.trim()
  const t = transcript.trim()
  if (!s) return t
  if (!t) return s
  return `AI summary:\n${s}\n\n---\n\nTranscript:\n${t}`
}

/** Wraps a finished transcript as a text attachment for the chat composer. */
export function makeTranscriptFile(text: string, recordedAt: Date, durationSec: number): AttachedFile {
  const pad = (n: number) => String(n).padStart(2, '0')
  const stamp = `${recordedAt.getFullYear()}-${pad(recordedAt.getMonth() + 1)}-${pad(recordedAt.getDate())} ${pad(recordedAt.getHours())}.${pad(recordedAt.getMinutes())}`
  const mins = Math.max(1, Math.round(durationSec / 60))
  const bytes = new TextEncoder().encode(text).length
  return {
    name: `Voice notes ${stamp} (${mins} min).txt`,
    mimeType: 'text/plain',
    data: utf8ToBase64(text),
    size: bytes,
  }
}

/**
 * The recorder MIME type this browser supports. Chrome/Android record
 * webm/opus; iOS Safari records mp4 (AAC). Empty string = construct
 * MediaRecorder without a mimeType and take the platform default; null =
 * recording unsupported here.
 */
export function pickRecorderMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c
    } catch {
      // some engines throw on unknown types — treat as unsupported
    }
  }
  return ''
}

/** Human-readable message for a failed transcription API call. */
export function friendlyTranscribeError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err)
  try {
    const parsed = JSON.parse(raw) as { error?: { reason?: string; detail?: string } }
    const detail = parsed.error?.detail
    if (detail === 'transcription_not_configured') {
      return 'Transcription is not set up yet — the AssemblyAI key is missing on the server.'
    }
    if (detail === 'audio_too_large') return 'This recording segment was too large to transcribe.'
    if (detail === 'transcription_timeout') return 'Transcription took too long and was abandoned.'
    if (parsed.error?.reason) return `Transcription failed (${parsed.error.reason}).`
  } catch {
    // not a JSON API error — fall through
  }
  return 'Transcription failed — check your connection and try again.'
}
